import { AlertCircle, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { MultiSelectPopover } from "@/components/ui/multi-select-popover";
import { canTurnOn, capabilityState } from "./capabilityState";
import { choiceField, choiceSummary, isChosen } from "./skillChoices";
import { CardFooter, IconBadge, PrimaryButton, WizardCard } from "./visuals";
import { cn } from "@/utils/cn";

/**
 * Step 3: what the agent will do with the systems chosen in step 2. Each card
 * is one capability; it says the system it uses, waits for the WhatsApp, or
 * links back to the system it still needs. A capability that needs one choice
 * (the contract models) asks it on its own card; `attention` names the cards
 * still missing it, with the reason.
 */
export default function StepCapabilities({
  skills,
  enabled,
  values = {},
  attention = {},
  legalProviderLabel,
  onToggle,
  onChoice,
  onConnectSystems,
  onConfigureAutomation,
  onBack,
  onNext,
}) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {skills.map((skill) => {
          const { state, footer } = capabilityState(skill, { legalProviderLabel });
          const isAutomation = skill.kind !== "agent";
          const allowed = isAutomation ? ["automation", "pending", "in_use"].includes(state) : canTurnOn(state);
          const on = allowed && (skill.required || Boolean(enabled[skill.id]));
          const field = !isAutomation && !skill.required && on ? choiceField(skill) : null;
          const chosen = field ? isChosen(field, values[skill.id]?.[field.name]) : true;
          const problem = attention[skill.id];
          return (
            <WizardCard
              key={skill.id}
              as="article"
              muted={!allowed && state !== "later"}
              aria-label={skill.card.title}
              className={cn("min-h-[172px]", problem && "border-amber-400 ring-1 ring-amber-400 dark:border-amber-400/70 dark:ring-amber-400/70")}
            >
              <div className="flex items-center gap-3 px-5 pt-5">
                <IconBadge icon={skill.card.icon} tone={allowed ? skill.card.tone : "gray"} size="sm" className="h-10 w-10" />
                <h2 className="flex-1 text-base font-semibold text-foreground">{skill.card.title}</h2>
                {!isAutomation && (
                  <Switch
                    checked={on}
                    disabled={!allowed || skill.required}
                    onCheckedChange={(v) => onToggle(skill.id, v)}
                    aria-label={skill.required ? `${skill.card.title}, sempre ligado` : skill.card.title}
                  />
                )}
              </div>
              <p className="px-5 pb-4 pt-2.5 text-sm leading-relaxed text-muted-foreground">{skill.card.summary}</p>
              {problem && !field ? (
                <CardFooter tone="waiting" className="justify-between">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>{problem}</span>
                  </span>
                  {state === "blocked" && (
                    <button type="button" onClick={onConnectSystems} className="min-h-[32px] shrink-0 font-semibold text-foreground underline-offset-4 hover:underline">
                      Conectar
                    </button>
                  )}
                </CardFooter>
              ) : field ? (
                <CardFooter tone={chosen ? "neutral" : "waiting"} className="justify-between">
                  <span className="flex min-w-0 items-center gap-1.5">
                    {!chosen && <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
                    <span className="truncate">{chosen ? choiceSummary(field, values[skill.id]?.[field.name]) : problem || `Falta escolher: ${field.label}`}</span>
                  </span>
                  <MultiSelectPopover
                    align="end"
                    className="w-72"
                    options={field.options}
                    selected={values[skill.id]?.[field.name] || []}
                    onChange={(next) => onChoice(skill.id, field.name, next)}
                    emptyText="Nenhum modelo na ZapSign"
                    searchable={field.options.length > 6}
                    trigger={
                      <button type="button" aria-label={`${field.label} (${skill.card.title})`} className="min-h-[32px] shrink-0 font-semibold text-foreground underline-offset-4 hover:underline">
                        {chosen ? "Trocar" : "Escolher"}
                      </button>
                    }
                  />
                </CardFooter>
              ) : state === "automation" ? (
                <CardFooter className="justify-between">
                  <span>{footer}</span>
                  <button type="button" onClick={() => onConfigureAutomation(skill)} className="min-h-[32px] font-semibold text-foreground underline-offset-4 hover:underline">
                    Configurar
                  </button>
                </CardFooter>
              ) : state === "waiting" || state === "later" || state === "pending" ? (
                <CardFooter tone="waiting">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  {footer}
                </CardFooter>
              ) : state === "blocked" ? (
                <CardFooter className="justify-between">
                  <span>{footer}</span>
                  <button type="button" onClick={onConnectSystems} className="min-h-[32px] font-semibold text-foreground underline-offset-4 hover:underline">
                    Conectar
                  </button>
                </CardFooter>
              ) : (
                <CardFooter>{footer}</CardFooter>
              )}
            </WizardCard>
          );
        })}
      </div>

      <div className="mt-9 flex flex-wrap items-center justify-end gap-5">
        <button type="button" onClick={onBack} className="min-h-[44px] text-[15px] text-muted-foreground hover:text-foreground">
          Voltar
        </button>
        <PrimaryButton onClick={onNext}>Continuar</PrimaryButton>
      </div>
    </>
  );
}
